import React, { useState, useEffect } from "react";
import "./CarrierProfileGeneral.css";
import PropTypes from "prop-types";
import { parseISO } from "date-fns";
import { Box, Typography, Grid } from "@mui/material";
import WarningMessage from "../../WarningMessage/WarningMessage";
import CarrierNetwork from "./CarrierNetwork/CarrierNetwork";
import CarrierProfileInsuranceIndicator from "./CarrierProfileInsuranceIndicator/CarrierProfileInsuranceIndicator";
import CarrierProfileScorecard from "./CarrierProfileScorecard/CarrierProfileScorecard";
import CarrierBookPill from "../../Pills/CarrierBookPill/CarrierBookPill";
import CarrierSearchSwap from "./CarrierSearchSwap/CarrierSearchSwap";
import useCarrierTranslations from "../../../hooks/useCarrierTranslations/useCarrierTranslations";
import { formatTitleCase, formatCurrency } from "../../../utils/formatHelpers";

export default function CarrierProfileGeneral({
  carrier,
  getCarrier,
  setIsUpdating
}) {
  const { safetyRatings } = useCarrierTranslations();
  const [cargoDate, setCargoDate] = useState("");
  const [liabilityDate, setLiabilityDate] = useState("");

  useEffect(() => {
    setCargoDate(parseISO(carrier.cargoExpiration));
    setLiabilityDate(parseISO(carrier.liabilityExpiration));
  }, [carrier]);

  return (
    <Box sx={{ backgroundColor: "black_opacity.05" }}>
      <Grid container sx={{ padding: "0 24px" }}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex" }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CarrierSearchSwap carrier={carrier} />
                <Box sx={{ marginLeft: "24px" }}>
                  <CarrierBookPill
                    carrierName={carrier.name}
                    carrierStatus={carrier.status}
                    highwayStatus={carrier.drsPayee?.highway_status}
                    highwayRuleAssessment={carrier.drsPayee?.highway_rule_assessment}
                  />
                </Box>
                {carrier.noDispatch &&
                  <Box sx={{ marginLeft: "20px" }}>
                    <WarningMessage text="Not Approved for Dispatch" displayText />
                  </Box>
                }
              </Box>
              <Box sx={{ display: "flex" }}>
                <Typography
                  variant="h4"
                  color="black_opacity.70"
                >
                  {formatTitleCase(carrier.city)}, {carrier.state}
                </Typography>
                <Typography
                  variant="h4"
                  color="black_opacity.70"
                  sx={{ margin: "0 6px" }}
                >
                  •
                </Typography>
                <Typography
                  variant="h4"
                  color="black_opacity.70"
                >
                  MC {carrier.mcNumber}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ marginLeft: "auto" }}>
              <CarrierNetwork carrier={carrier} getCarrier={getCarrier} setIsUpdating={setIsUpdating} />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={9} sx={{ padding: "12px 0" }}>
          <Grid container>
            <Grid item xs={12} sm={6} md={4}>
              <Box className="Carrier-general-detail-container">
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                >
                  Carrier ID
                </Typography>
                <span className="Carrier-general-detail-value">
                  <Typography
                    variant="body1"
                    color="black_opacity.100"
                  >
                    {carrier.id}
                  </Typography>
                </span>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box className="Carrier-general-detail-container">
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                >
                  DOT Number
                </Typography>
                <span className="Carrier-general-detail-value">
                  <Typography
                    variant="body1"
                    color="black_opacity.100"
                  >
                    {carrier.dotNumber}
                  </Typography>
                </span>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box className="Carrier-general-detail-container">
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                >
                  Safety Rating
                </Typography>
                <span className="Carrier-general-detail-value">
                  <Typography
                    variant="body1"
                    color="black_opacity.100"
                  >
                    {safetyRatings[carrier.safetyRating]}
                  </Typography>
                </span>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box className="Carrier-general-detail-container">
                <Box sx={{ display: "flex" }}>
                  <Typography
                    variant="subhead1"
                    color="black_opacity.100"
                    sx={{ marginRight: "4px" }}
                  >
                    Cargo Insurance
                  </Typography>
                  <Box sx={{ height: "20px" }}>
                    <CarrierProfileInsuranceIndicator date={cargoDate} />
                  </Box>
                </Box>
                <span className="Carrier-general-detail-value">
                  <Typography
                    variant="body1"
                    color="black_opacity.100"
                  >
                    {formatCurrency(carrier.cargoAmount)}
                  </Typography>
                </span>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box className="Carrier-general-detail-container">
                <Box sx={{ display: "flex" }}>
                  <Typography
                    variant="subhead1"
                    color="black_opacity.100"
                    sx={{ marginRight: "4px" }}
                  >
                    Auto Liability Insurance
                  </Typography>
                  <Box sx={{ height: "20px" }}>
                    <CarrierProfileInsuranceIndicator date={liabilityDate} />
                  </Box>
                </Box>
                <span className="Carrier-general-detail-value">
                  <Typography
                    variant="body1"
                    color="black_opacity.100"
                  >
                    {formatCurrency(carrier.liabilityAmount)}
                  </Typography>
                </span>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} />
          </Grid>
        </Grid>
        <Grid item xs={12} md={3} sx={{ padding: "12px 0" }}>
          <Box className="Carrier-general-detail-container">
            <Typography
              variant="subhead1"
              color="black_opacity.100"
            >
              Address
            </Typography>
            <span className="Carrier-general-detail-value">
              <Typography
                variant="body1"
                color="black_opacity.100"
              >
                {carrier.address1}
              </Typography>
            </span>
            <span className="Carrier-general-detail-value">
              <Typography
                variant="body1"
                color="black_opacity.100"
              >
                {carrier.address2}
              </Typography>
            </span>
            <span className="Carrier-general-detail-value">
              <Typography
                variant="body1"
                color="black_opacity.100"
              >
                {formatTitleCase(carrier.city)}, {carrier.state} {carrier.zipCode}
              </Typography>
            </span>
          </Box>
        </Grid>
        <CarrierProfileScorecard activeCarrier={carrier} />
      </Grid>
    </Box>
  );
}

CarrierProfileGeneral.propTypes = {
  carrier: PropTypes.object.isRequired,
  getCarrier: PropTypes.func.isRequired,
  setIsUpdating: PropTypes.func.isRequired
};